/** La lista de proyectos, con su avance. */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { ErrorDeApi, api } from '../api'
import { Cargando, Dialogo, Error, EtiquetaDeEstado, EtiquetaDePrioridad, Vacio } from '../componentes/piezas'
import { usePortal } from '../contexto'
import { PRIORIDAD, fecha } from '../textos'
import type { Prioridad, Proyecto } from '../tipos'

export default function Proyectos() {
  const { ruta, servidor, puedo, avisar } = usePortal()
  const [proyectos, setProyectos] = useState<Proyecto[] | null>(null)
  const [error, setError] = useState('')
  const [nuevo, setNuevo] = useState(false)

  const cargar = useCallback(async () => {
    try {
      setProyectos(await api.get<Proyecto[]>(`${ruta}/proyectos`))
      setError('')
    } catch (e) {
      setError((e as ErrorDeApi).message)
    }
  }, [ruta])

  useEffect(() => {
    void cargar()
  }, [cargar])

  return (
    <>
      <header className="cabecera">
        <h1>Proyectos</h1>
        <div className="cabecera__acciones">
          {puedo('gestionar_proyectos') && (
            <button className="boton boton--principal" onClick={() => setNuevo(true)}>
              Nuevo proyecto
            </button>
          )}
        </div>
      </header>

      <div className="contenido">
        {error && <Error>{error}</Error>}
        {!proyectos && !error && <Cargando filas={3} />}

        {proyectos && proyectos.length === 0 && (
          <div className="tarjeta">
            <Vacio titulo="Todavía no hay proyectos">
              <p>Salen al convertir una solicitud aprobada, o se crean a mano.</p>
            </Vacio>
          </div>
        )}

        {proyectos && proyectos.length > 0 && (
          <ul className="lista tarjeta">
            {proyectos.map((p) => (
              <li key={p.numero}>
                <Link className="fila" to={`/${servidor.id}/proyectos/${p.numero}`}>
                  <EtiquetaDePrioridad valor={p.prioridad} />
                  <div className="fila__principal">
                    <div className="fila__titulo">{p.nombre}</div>
                    <div className="fila__meta">
                      <span className="codigo">{p.codigo}</span>
                      {p.solicitud && <span>de {p.solicitud}</span>}
                      {p.responsable && <span>{p.responsable.nombre}</span>}
                      <span>
                        {p.tareas_hechas}/{p.tareas} {p.tareas === 1 ? 'tarea' : 'tareas'}
                      </span>
                      {p.comprometida && <span>comprometido para {fecha(p.comprometida)}</span>}
                    </div>
                  </div>
                  <EtiquetaDeEstado texto={p.estado_texto} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {nuevo && (
        <NuevoProyecto
          alCerrar={() => setNuevo(false)}
          alCrear={(codigo) => {
            setNuevo(false)
            avisar(`Creado ${codigo}.`, 'bien')
            void cargar()
          }}
        />
      )}
    </>
  )
}

function NuevoProyecto({
  alCerrar,
  alCrear,
}: {
  alCerrar: () => void
  alCrear: (codigo: string) => void
}) {
  const { ruta, departamentos } = usePortal()
  const [nombre, setNombre] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [prioridad, setPrioridad] = useState<Prioridad>('medium')
  const [departamento, setDepartamento] = useState('')
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  async function enviar() {
    if (!nombre.trim()) {
      setError('El proyecto necesita un nombre.')
      return
    }
    setEnviando(true)
    setError('')
    try {
      const creado = await api.post<Proyecto>(`${ruta}/proyectos`, {
        nombre,
        descripcion,
        prioridad,
        departamento_id: departamento ? Number(departamento) : null,
      })
      alCrear(creado.codigo)
    } catch (e) {
      setError((e as ErrorDeApi).message)
      setEnviando(false)
    }
  }

  return (
    <Dialogo
      titulo="Nuevo proyecto"
      alCerrar={alCerrar}
      pie={
        <>
          <button className="boton" onClick={alCerrar} disabled={enviando}>Cancelar</button>
          <button className="boton boton--principal" onClick={enviar} disabled={enviando}>
            {enviando ? 'Creando…' : 'Crear'}
          </button>
        </>
      }
    >
      {error && <Error>{error}</Error>}
      <div className="campo">
        <label htmlFor="n">Nombre</label>
        <input id="n" value={nombre} autoFocus onChange={(e) => setNombre(e.target.value)} />
      </div>
      <div className="campo">
        <label htmlFor="d">Descripción</label>
        <textarea id="d" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
      </div>
      <div className="campo">
        <label htmlFor="p">Prioridad</label>
        <select id="p" value={prioridad} onChange={(e) => setPrioridad(e.target.value as Prioridad)}>
          {Object.entries(PRIORIDAD).map(([clave, texto]) => (
            <option key={clave} value={clave}>{texto}</option>
          ))}
        </select>
      </div>
      {departamentos.length > 0 && (
        <div className="campo">
          <label htmlFor="dep">Departamento</label>
          <select id="dep" value={departamento} onChange={(e) => setDepartamento(e.target.value)}>
            <option value="">Sin departamento</option>
            {departamentos.map((d) => (
              <option key={d.id} value={d.id}>{d.nombre}</option>
            ))}
          </select>
        </div>
      )}
    </Dialogo>
  )
}
